import React from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import type { Incident } from '../../services/incidentsApi';
import { resolveSafetyEpisodeCopy } from './incidentSafetyEpisodeCopy';

export type IncidentSafetyEpisodeSummarySource = Pick<
  Incident,
  | 'incident_type'
  | 'episode_count'
  | 'observation_count'
  | 'active_episode_count'
  | 'observed_track_count'
  | 'evidence_count'
  | 'last_observed_at'
  | 'safety_episodes_available'
>;

export function isSafetyEpisodeSummaryUnavailable(
  incident: IncidentSafetyEpisodeSummarySource,
): boolean {
  if (incident.safety_episodes_available === false) return true;
  if (incident.episode_count === null || incident.episode_count === undefined) {
    return true;
  }
  return false;
}

function formatMetricValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

type MetricTileProps = {
  label: string;
  value: string;
};

const MetricTile: React.FC<MetricTileProps> = ({ label, value }) => (
  <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
      {value}
    </Typography>
  </Box>
);

export type IncidentSafetyEpisodeSummaryPanelProps = {
  incident: IncidentSafetyEpisodeSummarySource;
  episodeFamily?: string | null;
};

const IncidentSafetyEpisodeSummaryPanel: React.FC<IncidentSafetyEpisodeSummaryPanelProps> = ({
  incident,
  episodeFamily,
}) => {
  const copy = resolveSafetyEpisodeCopy(incident.incident_type, episodeFamily);
  if (isSafetyEpisodeSummaryUnavailable(incident)) {
    return (
      <Alert
        severity="info"
        data-testid="safety-episode-summary-unavailable"
        sx={{ mb: 1.5, borderRadius: 2 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {copy.unavailableTitle}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {copy.unavailableBody}
        </Typography>
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      data-testid="safety-episode-summary-panel"
      sx={{ p: 1.25, mb: 1.5, borderRadius: 2, bgcolor: '#fcfcfd' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <MetricTile
          label={copy.qualifyingEpisodes}
          value={formatMetricValue(incident.episode_count)}
        />
        <MetricTile
          label={copy.qualifyingObservations}
          value={formatMetricValue(incident.observation_count)}
        />
        <MetricTile
          label={copy.activeEpisodes}
          value={formatMetricValue(incident.active_episode_count)}
        />
        <MetricTile
          label={copy.observedTracks}
          value={formatMetricValue(incident.observed_track_count)}
        />
        <MetricTile
          label={copy.evidenceCaptured}
          value={formatMetricValue(incident.evidence_count)}
        />
        <MetricTile
          label={copy.lastObserved}
          value={formatTimestamp(incident.last_observed_at)}
        />
      </Box>
    </Paper>
  );
};

export default IncidentSafetyEpisodeSummaryPanel;
